import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

const AnimatedTitle = ({ animate = true }) => {
  const styles = `
    .title-text {
      font-family: 'Orbitron', sans-serif;
      font-weight: 700;
      fill: #ffffff;
      filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
    }
    
    .jp-text {
      font-family: 'Noto Sans JP', sans-serif;
      font-weight: 400;
      fill: #a8b8ff;
    }

    .cosmic-gradient {
      stop-color: #4a90e2;
      animation: cosmicPulse 3s infinite;
    }

    @keyframes cosmicPulse {
      0% { stop-color: #4a90e2; }
      50% { stop-color: #7b5ee7; }
      100% { stop-color: #4a90e2; }
    }
  `;

  const mainRef = useRef(null);

  useEffect(() => {
    if (!animate || !mainRef.current) return;

    const mainTitle = mainRef.current.querySelector('.main-title');
    const subTitle = mainRef.current.querySelector('.sub-title');
    const jpTexts = mainRef.current.querySelectorAll('.jp-text');

    // Initial state
    gsap.set([mainTitle, subTitle, jpTexts], {
      opacity: 0,
      y: 20
    });

    // Animation timeline
    const tl = gsap.timeline();

    // Main title animation
    tl.to(mainTitle, {
      opacity: 1,
      y: 0,
      duration: 1.2,
      ease: 'power3.out'
    });

    // Subtitle animation
    tl.to(subTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power2.out'
    }, '-=0.8');

    // Japanese text animation
    jpTexts.forEach((el, index) => {
      tl.to(el, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        ease: 'power2.out'
      }, `-=${0.2}`);
    });

    // Continuous hover animation
    gsap.to([mainTitle, subTitle], {
      y: '+=5',
      duration: 2,
      yoyo: true,
      repeat: -1,
      ease: 'power1.inOut'
    });

    // Glow animations
    const addGlow = () => {
      gsap.to(mainTitle, {
        filter: 'drop-shadow(0 0 25px rgba(74, 144, 226, 0.8)) drop-shadow(0 0 45px rgba(74, 144, 226, 0.4))',
        duration: 2,
        repeat: -1,
        yoyo: true
      });

      gsap.to(subTitle, {
        filter: 'drop-shadow(0 0 15px rgba(123, 94, 231, 0.8)) drop-shadow(0 0 30px rgba(123, 94, 231, 0.4))',
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        delay: 0.5
      });

      jpTexts.forEach((el, index) => {
        gsap.to(el, {
          filter: 'drop-shadow(0 0 10px rgba(168, 184, 255, 0.8))',
          duration: 1.8,
          repeat: -1,
          yoyo: true,
          delay: index * 0.2
        });
      });
    };

    tl.call(addGlow);
  }, [animate]);

  return (
    <div className="w-[400px] h-[200px] pointer-events-none">
      <style>{styles}</style>
      <svg ref={mainRef} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200" className="w-full h-full">
        <defs>
          <style type="text/css">
            @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&family=Noto+Sans+JP:wght@400&display=swap');
          </style>
          <linearGradient id="titleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" className="cosmic-gradient" />
            <stop offset="100%" style={{ stopColor: '#7b5ee7' }} />
          </linearGradient>
        </defs>

        <g transform="translate(200,100)" textAnchor="middle">
          <text
            className="title-text animate-el main-title"
            x="0"
            y="-20"
            fontSize="42"
            style={{ fill: 'url(#titleGradient)' }}
          >
            INTERSTELLAR
          </text>

          <text
            className="title-text animate-el sub-title"
            x="0"
            y="25"
            fontSize="36"
            style={{ fill: 'url(#titleGradient)' }}
          >
            LOVE
          </text>

          {/* Japanese Text */}
          {['イ','ン','タ','ー','ス','テ','ラ','ー','ラ','ブ'].map((char, index) => (
            <text
              key={index}
              className="jp-text animate-el"
              x={-90 + (index * 20)}
              y="60"
              fontSize="14"
            >
              {char}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default AnimatedTitle;
